import {
  Avatar,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Box,
  Tooltip,
  Stack,
  Typography,
  Tabs,
  Tab,
  Drawer,
  useTheme,
  useMediaQuery,
  alpha,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  Pagination,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import React, { useEffect, useState } from 'react'
import ClinicAppointmentTypeForm, { ITEMS_TYPE_OPTIONS } from './ClinicAppointmentTypeForm'
import { CSmartTable } from '@coreui/react-pro'
import {
  getCSmartTableColumnData,
  isValidNumber,
} from 'src/consumer/helpers/utilFunctions'
import {
  getPriceStringWithCurrency,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import Add from '@mui/icons-material/Add'
import Close from '@mui/icons-material/Close'
import Delete from '@mui/icons-material/Delete'
import Edit from '@mui/icons-material/Edit'
import Save from '@mui/icons-material/Save'
import Cancel from '@mui/icons-material/Cancel'
import Search from '@mui/icons-material/Search'
import { default as CloseIcon } from '@mui/icons-material/Close'
import {
  BOOKED_EVENT_MODES,
  CLINIC_EVENT_TYPES_FOR_SERVICES,
  CLINIC_SERVICES_ITEMS_TYPES,
} from 'src/consumer/constants'
import { formatScreeningSpecialtyLabel } from 'src/consumer/helpers/screeningSpecialtyUtils'
import { isAppointmentListingItemGettingAdded } from './util'
import { SelectTableWrapper } from './styledComponents'
import { toast } from 'react-toastify'
import { postCPData } from 'src/clinic/services'
import { postSPDataWithParams } from 'src/supportPortal/services/services'
import { getSupportClinicApptTypeSaveError } from './supportClinicAppointmentTypeSaveErrors'
import { mediaURL } from 'src/consumer/services'

const StyledTableWrapper = styled(Box)(({ theme }) => ({
  borderRadius: '12px',
  overflow: 'hidden',
  border: '1px solid',
  borderColor: theme.palette.divider,
  background: '#ffffff',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  '& .table': {
    marginBottom: 0,
  },
  '& thead th': {
    background: 'linear-gradient(135deg, rgba(17, 39, 71, 0.06) 0%, rgba(30, 58, 138, 0.04) 100%)',
    borderBottom: `2px solid ${theme.palette.divider}`,
    fontWeight: 600,
    fontSize: '0.8125rem',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: theme.palette.text.secondary,
    padding: theme.spacing(2, 2),
  },
  '& tbody tr': {
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
    '&.table-active': {
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  },
  '& tbody td': {
    padding: theme.spacing(2, 2),
    verticalAlign: 'middle',
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
    fontSize: '0.875rem',
  },
}))

const ActionButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: theme.spacing(0.75, 1.5),
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  boxShadow: 'none',
  '&.save-button': {
    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.95) 0%, rgba(22, 163, 74, 0.95) 100%)',
    color: '#ffffff',
    '&:hover': {
      background: 'linear-gradient(135deg, rgba(34, 197, 94, 1) 0%, rgba(22, 163, 74, 1) 100%)',
      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
    },
  },
  '&.cancel-button': {
    background: alpha(theme.palette.error.main, 0.1),
    color: theme.palette.error.main,
    '&:hover': {
      background: alpha(theme.palette.error.main, 0.2),
    },
  },
}))

const EditIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  padding: theme.spacing(0.75),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    transform: 'scale(1.1)',
  },
  transition: 'all 0.2s ease',
}))

const AddButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(17, 39, 71, 0.95) 0%, rgba(30, 58, 138, 0.95) 100%)',
  color: '#ffffff',
  borderRadius: '10px',
  padding: theme.spacing(1, 2.5),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.9375rem',
  boxShadow: '0 4px 12px rgba(17, 39, 71, 0.25)',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(17, 39, 71, 1) 0%, rgba(30, 58, 138, 1) 100%)',
    boxShadow: '0 6px 16px rgba(17, 39, 71, 0.35)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}))

const StatusChip = styled(Chip)(({ theme }) => ({
  height: '24px',
  fontSize: '0.75rem',
  fontWeight: 500,
  borderRadius: '6px',
  '&.not-set': {
    background: alpha(theme.palette.warning.main, 0.1),
    color: theme.palette.warning.dark,
    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
  },
}))

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: 'linear-gradient(135deg, rgba(17, 39, 71, 0.02) 0%, rgba(30, 58, 138, 0.01) 100%)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
}))

const DrawerContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(3),
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.2),
    borderRadius: '4px',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.3),
    },
  },
}))

const DrawerFooter = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.divider}`,
  background: '#ffffff',
  position: 'sticky',
  bottom: 0,
  zIndex: 1,
}))

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `2px solid ${alpha(theme.palette.divider, 0.5)}`,
  marginBottom: theme.spacing(3),
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
    background: 'linear-gradient(135deg, rgba(17, 39, 71, 0.95) 0%, rgba(30, 58, 138, 0.95) 100%)',
  },
}))

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.9375rem',
  minHeight: 48,
  padding: theme.spacing(1.5, 3),
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  transition: 'all 0.2s ease',
}))

const ITEMS_PER_PAGE = 10
const SCREENING_EVENT_TYPE = 'screening'

/** Item is "Not Set" when it's a template placeholder (has templateId but no saved id). */
function isNotSetItem(item) {
  return Boolean(item?.templateId && !item?.id)
}

export default function AppointmentListingTable({
  clinicApptTypes,
  defaultTemplates,
  clinicLocation,
  clinicBrand,
  loadingPageData,
  onRowClick,
  refreshData,
  clinicId,
  clinicBrandId,
  saveAppointmentApiSignature = '/api/secure/clinic-appointment/save',
}) {
  const [showForm, setShowForm] = useState(false)
  const [toShowItemForm, setToShowItem] = useState()
  const [localClinicApptTypes, setLocalClinicApptTypes] = useState()
  const [saving, setSaving] = useState(false)
  const [selectedTab, setSelectedTab] = useState('all')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showNotSet, setShowNotSet] = useState(true)
  const [page, setPage] = useState(1)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  function closeDialog() {
    setShowForm(false)
  }

  function openEditDrawer(item, idx) {
    const originalIdx = item?._originalIndex !== undefined ? item._originalIndex : idx
    const originalItem = localClinicApptTypes?.[originalIdx]
    setEditingItem(originalItem)
    setDrawerOpen(true)
  }

  function closeEditDrawer() {
    setDrawerOpen(false)
    setEditingItem(null)
  }


  function showSaveError(code, serverMessage) {
    const isSupportApi = saveAppointmentApiSignature?.includes('/api/secure/cc/support/')
    if (isSupportApi && code != null) {
      const { message, action } = getSupportClinicApptTypeSaveError(code)
      toast.error(action ? `${message} ${action}` : message, { autoClose: 6000 })
    } else {
      toast.error(serverMessage || 'Failed to save. Please try again.', { autoClose: 5000 })
    }
  }

  async function saveAppointmentType(formData) {
    const formDataToSave = { ...formData, clinicLocationId: clinicId, clinicBrandId: clinicBrandId }
    if (formData?.id) formDataToSave.id = formData.id
    setSaving(true)
    const isSupportPortalAPI = saveAppointmentApiSignature?.includes('/api/secure/cc/support/')
    try {
      const resp = isSupportPortalAPI
        ? await postSPDataWithParams(saveAppointmentApiSignature, formDataToSave, {}, undefined)
        : await postCPData(saveAppointmentApiSignature, formDataToSave)

      const isAxiosError = resp && !resp?.status && resp?.response
      const errorPayload = isAxiosError ? resp.response?.data : resp?.data
      const status = isAxiosError ? resp.response?.status : resp?.status

      if (status === 200) {
        if (isCommonResponseSuccessfulV2(resp)) {
          closeEditDrawer()
          refreshData?.()
        } else {
          const code = errorPayload?.code ?? resp?.data?.code
          const msg = errorPayload?.message ?? resp?.data?.message
          showSaveError(code, msg)
        }
      } else if (errorPayload) {
        showSaveError(errorPayload?.code, errorPayload?.message)
      } else {
        showSaveError(null, 'Request failed. Please try again.')
      }
    } catch (err) {
      showSaveError(err?.response?.data?.code, err?.response?.data?.message)
    } finally {
      setSaving(false)
    }
  }

  function addNewAppType() {
    setEditingItem(null)
    setDrawerOpen(true)
  }

  function resetApptTypes() {
    setLocalClinicApptTypes(clinicApptTypes)
  }

  useEffect(() => {
    setLocalClinicApptTypes(clinicApptTypes)
  }, [clinicApptTypes])

  // Filter by tab, search, and showNotSet; map to original indices
  const filteredApptTypes = React.useMemo(() => {
    if (!localClinicApptTypes) return []
    let list = localClinicApptTypes
      .map((item, idx) => ({ ...item, _originalIndex: idx }))
      .filter((item) => selectedTab === 'all' || item?.type === selectedTab)
    const q = (searchQuery || '').trim().toLowerCase()
    if (q) {
      list = list.filter(
        (item) =>
          (item?.title || '').toLowerCase().includes(q) ||
          (item?.type === SCREENING_EVENT_TYPE
            ? formatScreeningSpecialtyLabel(item?.targetSpecialtySlug).toLowerCase().includes(q)
            : false) ||
          (CLINIC_EVENT_TYPES_FOR_SERVICES.find((t) => t.id === item?.type)?.title || '')
            .toLowerCase()
            .includes(q),
      )
    }
    if (!showNotSet) {
      list = list.filter((item) => !isNotSetItem(item))
    }
    return list
  }, [localClinicApptTypes, selectedTab, searchQuery, showNotSet])

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [selectedTab, searchQuery, showNotSet])

  const totalFiltered = filteredApptTypes.length
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ITEMS_PER_PAGE))
  const pageSafe = Math.min(Math.max(1, page), totalPages)
  const paginatedApptTypes = React.useMemo(() => {
    const start = (pageSafe - 1) * ITEMS_PER_PAGE
    return filteredApptTypes.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredApptTypes, pageSafe])

  // Remove inline editing - we'll use drawer instead
  // All scopedColumns should now only show view mode

  // Get unique service types from data
  const availableServiceTypes = React.useMemo(() => {
    if (!localClinicApptTypes) return []
    const types = new Set(localClinicApptTypes.map((item) => item?.type).filter(Boolean))
    return Array.from(types)
  }, [localClinicApptTypes])

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue)
    resetApptTypes()
  }

  // Tab counts: same filters as filteredApptTypes (search + showNotSet) per tab
  const tabCounts = React.useMemo(() => {
    if (!localClinicApptTypes) return { all: 0 }
    const base = (tabValue) => {
      let list =
        tabValue === 'all'
          ? localClinicApptTypes
          : localClinicApptTypes.filter((item) => item?.type === tabValue)
      const q = (searchQuery || '').trim().toLowerCase()
      if (q) {
        list = list.filter(
          (item) =>
            (item?.title || '').toLowerCase().includes(q) ||
            (item?.type === SCREENING_EVENT_TYPE
              ? formatScreeningSpecialtyLabel(item?.targetSpecialtySlug).toLowerCase().includes(q)
              : false) ||
            (CLINIC_EVENT_TYPES_FOR_SERVICES.find((t) => t.id === item?.type)?.title || '')
              .toLowerCase()
              .includes(q),
        )
      }
      if (!showNotSet) list = list.filter((item) => !isNotSetItem(item))
      return list.length
    }
    const counts = { all: base('all') }
    CLINIC_EVENT_TYPES_FOR_SERVICES.forEach((type) => {
      counts[type.id] = base(type.id)
    })
    return counts
  }, [localClinicApptTypes, searchQuery, showNotSet])

  const tabs = [
    { value: 'all', label: 'All Services', count: tabCounts.all },
    ...CLINIC_EVENT_TYPES_FOR_SERVICES.map((type) => ({
      value: type.id,
      label: type.title,
      count: tabCounts[type.id] ?? 0,
    })),
  ]

  return (
    <>
      {/* Search, filter toggle, and Add */}
      <Box sx={{ mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by title or service type…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 280,
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: theme.palette.background.paper,
              },
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showNotSet}
                onChange={(e) => setShowNotSet(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="body2" fontWeight={500}>
                Show Not Set
              </Typography>
            }
          />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <AddButton
              onClick={() => {
                addNewAppType()
              }}
              startIcon={<Add />}
            >
              Add New Service
            </AddButton>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <StyledTabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ flex: 1 }}
        >
          {tabs.map((tab) => (
            <StyledTab
              key={tab.value}
              value={tab.value}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{tab.label}</span>
                  {tab.count > 0 && (
                    <Chip
                      label={tab.count}
                      size="small"
                      sx={{
                        height: 20,
                        minWidth: 20,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: selectedTab === tab.value
                          ? alpha(theme.palette.primary.main, 0.2)
                          : alpha(theme.palette.text.secondary, 0.1),
                        color: selectedTab === tab.value
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                      }}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </StyledTabs>
      </Box>
      <StyledTableWrapper>
        <SelectTableWrapper>
          <CSmartTable
            items={paginatedApptTypes}
            columns={columns()}
            loading={loadingPageData}
            tableProps={{
              hover: true,
              striped: true,
              responsive: true,
            }}
            itemsPerPage={ITEMS_PER_PAGE}
            scopedColumns={scopedColumns({
              openEditDrawer,
              saveChangedItem: saveAppointmentType,
              resetApptTypes,
              isSaving: saving,
              theme,
            })}
          />
        </SelectTableWrapper>
      </StyledTableWrapper>

      {/* Pagination */}
      {totalFiltered > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mt: 2,
            py: 2,
            px: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {(pageSafe - 1) * ITEMS_PER_PAGE + 1}–{Math.min(pageSafe * ITEMS_PER_PAGE, totalFiltered)} of{' '}
            {totalFiltered} service{totalFiltered !== 1 ? 's' : ''}
          </Typography>
          {totalPages > 1 && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
                Page {pageSafe} of {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={pageSafe}
                onChange={(_, p) => setPage(p)}
                color="primary"
                size="medium"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPagination-ul': { flexWrap: 'nowrap' },
                  '& .MuiPaginationItem-root': {
                    minWidth: 36,
                    height: 36,
                    borderRadius: '8px',
                    fontWeight: 600,
                  },
                }}
              />
            </Stack>
          )}
        </Box>
      )}
      
      {/* Edit Drawer */}
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={drawerOpen}
        onClose={closeEditDrawer}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 600,
            maxWidth: isMobile ? '100%' : '90vw',
            height: isMobile ? '90vh' : '100%',
            maxHeight: isMobile ? '90vh' : '100vh',
            borderTopLeftRadius: isMobile ? '20px' : 0,
            borderTopRightRadius: isMobile ? '20px' : 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
        ModalProps={{
          keepMounted: false,
        }}
      >
        {/* Drag Handle for Mobile */}
        {isMobile && (
          <Box
            sx={{
              width: '40px',
              height: '4px',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '2px',
              margin: '12px auto 0',
              cursor: 'grab',
              flexShrink: 0,
              '&:active': {
                cursor: 'grabbing',
              },
            }}
          />
        )}
        
        <DrawerHeader>
          <Typography variant="h6" fontWeight={600}>
            {editingItem ? 'Edit Service' : 'Add New Service'}
          </Typography>
          <IconButton
            onClick={closeEditDrawer}
            sx={{
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DrawerHeader>

        <DrawerContent>
          <ClinicAppointmentTypeForm
            clinicAppointmentType={editingItem}
            defaultTemplates={defaultTemplates}
            clinicLocation={clinicLocation}
            clinicBrand={clinicBrand}
            closeDialog={closeEditDrawer}
            refreshData={async () => {
              const fn = refreshData
              if (typeof fn === 'function') {
                const result = fn()
                if (result && typeof result.then === 'function') await result
              }
              closeEditDrawer()
            }}
            clinicId={clinicId}
            clinicBrandId={clinicBrandId}
            isDrawerMode={true}
            saveAppointmentApiSignature={saveAppointmentApiSignature}
          />
        </DrawerContent>
      </Drawer>

      {/* Legacy Dialog for Add New (can be removed if not needed) */}
      <Dialog open={showForm} onClose={closeDialog}>
        <ClinicAppointmentTypeForm
          clinicAppointmentType={!toShowItemForm ? null : toShowItemForm}
          defaultTemplates={defaultTemplates}
          clinicLocation={clinicLocation}
          clinicBrand={clinicBrand}
          closeDialog={closeDialog}
          refreshData={refreshData}
          clinicId={clinicId}
          clinicBrandId={clinicBrandId}
          saveAppointmentApiSignature={saveAppointmentApiSignature}
        />
      </Dialog>
    </>
  )
}

// Modernized columns: combined, high-signal columns for better scanability
const columns = () => [
  getCSmartTableColumnData({
    key: 'service',
    label: 'Service',
    filter: false,
    sorter: false,
    _style: { minWidth: '260px' },
  }),
  getCSmartTableColumnData({
    key: 'schedule',
    label: 'Schedule',
    filter: false,
    sorter: false,
    _style: { width: '120px' },
  }),
  getCSmartTableColumnData({
    key: 'pricing',
    label: 'Pricing',
    filter: false,
    sorter: false,
    _style: { width: '140px' },
  }),
  getCSmartTableColumnData({
    key: 'practitioner',
    label: 'Practitioner',
    filter: false,
    sorter: false,
    _style: { minWidth: '140px' },
  }),
  getCSmartTableColumnData({
    key: 'itemsType',
    label: 'Items',
    filter: false,
    sorter: false,
    _style: { width: '90px' },
  }),
  getCSmartTableColumnData({
    key: 'actions',
    label: '',
    filter: false,
    sorter: false,
    _style: { width: '72px' },
  }),
]

// Build a single pricing line: main price, optional intro, optional GST
function formatPricingLine(item) {
  const pd = item?.pricingDetail
  const currency = pd?.currencyId ?? pd?.currency
  const price = pd?.price
  const intro = pd?.introductoryPrice
  const vat = pd?.vatPercentage
  const hasPrice = isValidNumber(price)
  if (!hasPrice) return null
  const main = getPriceStringWithCurrency(price, currency)
  const hasIntro = isValidNumber(intro) && Number(intro) !== Number(price)
  const parts = []
  if (hasIntro) parts.push(`Intro ${getPriceStringWithCurrency(intro, currency)}`)
  parts.push(main)
  if (isValidNumber(vat)) parts.push(`${vat}% GST`)
  return parts.join(' · ')
}

const scopedColumns = ({
  openEditDrawer,
  saveChangedItem,
  resetApptTypes,
  isSaving,
  theme: themeProp,
}) => {
  const theme = themeProp || {}
  return {
    service: (item) => {
      const isNew = !item?.id
      const isTemplateBased = Boolean(item?.templateId)
      const typeLabel = CLINIC_EVENT_TYPES_FOR_SERVICES.find((t) => t.id === item?.type)?.title
      const specialtyLabel =
        item?.type === SCREENING_EVENT_TYPE
          ? formatScreeningSpecialtyLabel(item?.targetSpecialtySlug)
          : null
      return (
        <td>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Avatar
              src={item?.iconId ? `${mediaURL}${item.iconId}` : null}
              variant="rounded"
              sx={{
                width: 40,
                height: 40,
                bgcolor: item?.iconId ? 'transparent' : alpha(theme.palette?.primary?.main ?? '#1976d2', 0.08),
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }} noWrap>
                {item?.title || '—'}
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ mt: 0.25 }}>
                {typeLabel && (
                  <Chip
                    label={typeLabel}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      backgroundColor: alpha(theme.palette?.primary?.main ?? '#1976d2', 0.1),
                      color: theme.palette?.primary?.main ?? '#1976d2',
                    }}
                  />
                )}
                {specialtyLabel && (
                  <Chip
                    label={specialtyLabel}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      backgroundColor: alpha(theme.palette?.info?.main ?? '#0284c7', 0.1),
                      color: theme.palette?.info?.dark ?? '#0369a1',
                    }}
                  />
                )}
                {isTemplateBased && (
                  <Chip
                    label="Standard"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      backgroundColor: alpha(theme.palette?.success?.main ?? '#2e7d32', 0.1),
                      color: theme.palette?.success?.dark ?? '#1b5e20',
                    }}
                  />
                )}
                {isNew && <StatusChip label="Not Set" className="not-set" size="small" />}
              </Stack>
            </Box>
          </Stack>
        </td>
      )
    },
    schedule: (item) => {
      const duration = item?.duration
      const modeLabel = BOOKED_EVENT_MODES?.[item?.mode]?.title
      const durationStr = duration != null ? `${duration} min` : '—'
      const modeStr = modeLabel || '—'
      return (
        <td>
          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
            {durationStr}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {modeStr}
          </Typography>
        </td>
      )
    },
    pricing: (item) => {
      const line = formatPricingLine(item)
      return (
        <td>
          <Typography variant="body2" sx={{ fontWeight: 500 }} color={line ? 'text.primary' : 'text.secondary'}>
            {line || 'Not set'}
          </Typography>
        </td>
      )
    },
    practitioner: (item) => (
      <td>
        <Typography variant="body2" color={item?.mentorName ? 'text.primary' : 'text.secondary'} noWrap>
          {item?.mentorName || '—'}
        </Typography>
      </td>
    ),
    itemsType: (item) => (
      <td>
        <Chip
          label={CLINIC_SERVICES_ITEMS_TYPES?.[item?.itemsType]?.title || '—'}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        />
      </td>
    ),
    actions: (item, idx) => {
      const isAdding = isAppointmentListingItemGettingAdded(item)
      const originalIdx = item?._originalIndex !== undefined ? item._originalIndex : idx

      if (isAdding) {
        return (
          <td>
            <Stack direction="row" spacing={1} alignItems="center">
              <ActionButton
                className="save-button"
                disabled={isSaving}
                onClick={() => {
                  saveChangedItem(item)
                }}
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                size="small"
              >
                {isSaving ? 'Creating...' : 'Create'}
              </ActionButton>
              <Tooltip title="Cancel">
                <IconButton
                  size="small"
                  onClick={() => {
                    resetApptTypes()
                  }}
                  sx={{
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  <Cancel />
                </IconButton>
              </Tooltip>
            </Stack>
          </td>
        )
      }

      return (
        <td>
          <Tooltip title="Edit">
            <EditIconButton
              onClick={() => {
                openEditDrawer(item, idx)
              }}
              size="small"
            >
              <Edit />
            </EditIconButton>
          </Tooltip>
        </td>
      )
    },
  }
}
